import { useState, Fragment } from 'react';
import { Loader2, GitCompare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtGbp(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}

function DecompositionRow({ r }) {
  const d = r.decomposition;
  if (!d) return null;
  return (
    <tr className="border-t bg-muted/20" data-testid="parity-decomposition-row">
      <td colSpan={6} className="p-2">
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            Decomposition{d.currency_basis_mismatch && (
              <Badge variant="destructive" className="ml-2">currency basis mismatch</Badge>
            )}
          </summary>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono">
            <div>Gross revenue: {d.gross_revenue_native} <span className="text-muted-foreground">native</span></div>
            <div>OTA commission: {d.ota_commission_native} <span className="text-muted-foreground">native</span></div>
            <div>Net revenue: {d.net_revenue_native} <span className="text-muted-foreground">native</span></div>
            <div>Owner-attributable: {d.owner_revenue_native} <span className="text-muted-foreground">native</span></div>
          </div>
          <div className="mt-1 font-mono text-muted-foreground">
            By booking currency: {Object.entries(d.revenue_native_by_booking_currency || {})
              .map(([cur, amt]) => `${cur} ${amt}`).join(', ') || '—'}
            {' '}(Property Portfolio's own declared currency: {d.property_portfolio_declared_currency})
          </div>
          <div className="mt-1 not-italic text-muted-foreground">
            <p>Group Dashboard renders: {d.group_dashboard_renders}</p>
            <p>Property Portfolio renders: {d.property_portfolio_renders}</p>
          </div>
        </details>
      </td>
    </tr>
  );
}

function DiffTable({ title, rows, emptyLabel, showDecomposition }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <p className="text-xs font-medium mb-1">{title}</p>
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div>
      {title && <p className="text-xs font-medium mb-1">{title} ({rows.length})</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-2">Property</th>
              <th className="text-left p-2">Entity (stamped)</th>
              <th className="text-center p-2">In GD scope</th>
              <th className="text-right p-2">Group Dashboard (GBP)</th>
              <th className="text-right p-2">Property Portfolio (GBP)</th>
              <th className="text-right p-2">Delta (GBP)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              if (!showDecomposition) {
                return (
                  <tr key={r.canonical_property_id} className="border-t" data-testid="parity-diff-row">
                    <td className="p-2">
                      {r.name || <em>(unnamed)</em>}
                      <div className="font-mono text-muted-foreground">{r.canonical_property_id}</div>
                    </td>
                    <td className="p-2 font-mono">{r.entity_id_stamped || '—'}</td>
                    <td className="p-2 text-center">
                      <Badge variant={r.in_group_dashboard_scope ? 'outline' : 'destructive'}>
                        {r.in_group_dashboard_scope ? 'yes' : 'no'}
                      </Badge>
                    </td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.group_dashboard_revenue_gbp)}</td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.property_portfolio_revenue_gbp)}</td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.delta_gbp)}</td>
                  </tr>
                );
              }
              return (
                <Fragment key={r.canonical_property_id}>
                  <tr className="border-t" data-testid="parity-diff-row">
                    <td className="p-2">
                      {r.name || <em>(unnamed)</em>}
                      <div className="font-mono text-muted-foreground">{r.canonical_property_id}</div>
                    </td>
                    <td className="p-2 font-mono">{r.entity_id_stamped || '—'}</td>
                    <td className="p-2 text-center">
                      <Badge variant={r.in_group_dashboard_scope ? 'outline' : 'destructive'}>
                        {r.in_group_dashboard_scope ? 'yes' : 'no'}
                      </Badge>
                    </td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.group_dashboard_revenue_gbp)}</td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.property_portfolio_revenue_gbp)}</td>
                    <td className="p-2 text-right font-mono">{fmtGbp(r.delta_gbp)}</td>
                  </tr>
                  <DecompositionRow r={r} />
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PortfolioRevenueParityPanel() {
  const defaultMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!month.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.portfolioRevenueParity({ month: month.trim() });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const diff = r && r.diff;
  const gd = r && r.group_dashboard;
  const pp = r && r.property_portfolio;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <GitCompare className="h-5 w-5 text-blue-600" />
          Group Dashboard vs Property Portfolio Revenue Parity (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For a given month, runs the Group Dashboard's property_portfolio scope computation and
          the Property Portfolio dataset endpoint's own computation against the same live data in
          the same request, then diffs them property by property. Read-only, no writes, applies
          no fixes -- the diff is the answer.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="w-36">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Month</label>
            <Input
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="YYYY-MM"
              className="font-mono"
            />
          </div>
          <Button onClick={run} disabled={loading || !month.trim()} data-testid="portfolio-revenue-parity-run">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <GitCompare className="h-4 w-4 mr-2" />
            )}
            Run
          </Button>
        </div>

        {r && (
          <div className="space-y-4" data-testid="portfolio-revenue-parity-result">
            <div className="flex flex-wrap gap-2 text-xs items-center">
              <Badge variant="outline">{r.month}</Badge>
              <Badge variant={r.is_actuals_month ? 'outline' : 'destructive'}>
                {r.is_actuals_month ? 'actuals month' : 'forecast month'}
              </Badge>
              <Badge variant={gd.reconciles ? 'outline' : 'destructive'}>
                self-check: {gd.reconciles ? 'reconciles' : 'does not reconcile'}
              </Badge>
              {(r.missing_fx_currencies || []).length > 0 && (
                <Badge variant="destructive">
                  missing FX: {r.missing_fx_currencies.join(', ')}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="border rounded-lg p-3 space-y-1">
                <p className="font-medium">Group Dashboard (property_portfolio scope)</p>
                <p>Booking revenue (any property, no entity gate): <span className="font-mono">{fmtGbp(gd.booking_revenue_gbp)}</span></p>
                <p>Non-property revenue (invoices/tx, entities <span className="font-mono">{(gd.scope_entity_ids || []).join(', ')}</span> only): <span className="font-mono">{fmtGbp(gd.non_property_revenue_gbp)}</span> ({(gd.non_property_records || []).length} records)</p>
                <p>Reconstructed total: <span className="font-mono">{fmtGbp(gd.reconstructed_total_gbp)}</span></p>
                <p>Authoritative (live endpoint) total: <span className="font-mono">{fmtGbp(gd.authoritative_scope_income_gbp)}</span></p>
                {!gd.reconciles && (
                  <p className="text-amber-600">
                    Residual (authoritative - reconstructed): <span className="font-mono">{fmtGbp(gd.residual_gbp)}</span> --
                    a revenue source this diagnostic doesn't model (e.g. BR OS bookings misattributed to a
                    property_portfolio entity).
                  </p>
                )}
              </div>
              <div className="border rounded-lg p-3 space-y-1">
                <p className="font-medium">Property Portfolio (dataset endpoint)</p>
                <p>Authoritative total: <span className="font-mono">{fmtGbp(pp.authoritative_total_gbp)}</span></p>
              </div>
            </div>

            {r.forecast_context && (
              <div className="border border-amber-300 dark:border-amber-800 rounded-lg p-3 text-xs space-y-1">
                <p className="font-medium">Forecast month context</p>
                <p className="text-muted-foreground">{r.forecast_context.note}</p>
                <p>
                  Property Portfolio page's forecast (GET /reports/forecast) for this month:{' '}
                  <span className="font-mono">
                    {r.forecast_context.in_forecast_window
                      ? fmtGbp(r.forecast_context.property_portfolio_page_forecast_revenue_gbp)
                      : 'outside the 90-day forecast window -- no figure available'}
                  </span>
                </p>
              </div>
            )}

            <DiffTable
              title="Present only in Property Portfolio (composition difference)"
              rows={diff.present_only_in_property_portfolio}
              emptyLabel="None -- every Property Portfolio property is also visible to Group Dashboard's scope."
            />
            <DiffTable
              title="Present only in Group Dashboard"
              rows={diff.present_only_in_group_dashboard}
              emptyLabel="None -- Group Dashboard surfaced no property absent from Property Portfolio."
            />
            <DiffTable
              title="Amount mismatch on shared properties (computation divergence)"
              rows={diff.amount_mismatches}
              emptyLabel="None -- every property present in both paths agrees to the penny."
              showDecomposition
            />

            <details>
              <summary className="text-xs font-medium cursor-pointer">
                All properties ({(r.properties || []).length})
              </summary>
              <div className="mt-2">
                <DiffTable title="" rows={r.properties} emptyLabel="No properties." />
              </div>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
